import {
  IonButton,
  IonContent,
  IonPage,
  IonRouterLink,
  NavContext
} from '@ionic/react';
import { useCallback, useContext } from 'react';

import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

import logo from '@/assets/images/logo/logo-with-icon.png';
import { ButtonContainer, Padding, Promo } from '@/components/common';
import { Text } from '@/components/memo';
import { useOnboarding } from '@/contexts';
import { useDimensions, useToast } from '@/hooks';
import { Onboarding } from '@/pages';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '@/utils/constants';
import {
  ONBOARDING_DELAY,
  ONBOARDING_MINIMUM_SCREEN_HEIGHT
} from '@/utils/onboarding';
import { ONBOARDING_STATUS } from '@/utils/onboarding';
import { ROUTES } from '@/utils/routes';

import './Welcome.css';

interface WelcomeProps {}

const Welcome: React.FC<WelcomeProps> = () => {
  const { navigate } = useContext(NavContext);
  const displayToast = useToast();
  const { status: onboardingStatus } = useOnboarding();
  const { height: screenHeight } = useDimensions();

  const openTermsOfService = () => {
    Browser.open({ url: TERMS_OF_SERVICE_URL });
  };

  const openPrivacyPolicy = () => {
    Browser.open({ url: PRIVACY_POLICY_URL });
  };

  const getStarted = useCallback(() => {
    Capacitor.isNativePlatform()
      ? navigate(ROUTES.GET_STARTED)
      : displayToast({
          message:
            'Sign up is only available on our mobile app. Please download it from the App Store or Google Play.',
          position: 'top'
        });
  }, [navigate]);

  const signIn = useCallback(() => {
    navigate(ROUTES.SIGN_IN);
  }, [navigate]);

  const copy = {
    getStartedButtonText: 'Get started',
    signInButtonText: 'Log in',
    note: (
      <Text>
        By clicking Get started, you agree to our{' '}
        <IonRouterLink color="primary" onClick={openTermsOfService}>
          Terms of Service
        </IonRouterLink>
        {' and '}
        <IonRouterLink color="primary" onClick={openPrivacyPolicy}>
          Privacy Policy
        </IonRouterLink>
        .
      </Text>
    )
  };

  /* Display onboarding when:
   * - Onboarding status is pending
   * - Screen height is greater than 721px
   */
  const showOnboarding: boolean =
    onboardingStatus === ONBOARDING_STATUS.PENDING &&
    (!screenHeight || screenHeight > ONBOARDING_MINIMUM_SCREEN_HEIGHT);

  return (
    <IonPage id="welcome">
      <IonContent scrollX={false} scrollY={false}>
        <Padding>
          <div className="welcome__promo-container">
            <Promo image={logo} />
          </div>
          <ButtonContainer>
            <IonButton
              id="get-started-button"
              onClick={getStarted}
              expand="block"
              fill="solid"
              size="default"
              color="primary"
              strong={true}
            >
              {copy.getStartedButtonText}
            </IonButton>
            <IonButton
              id="sign-in-button"
              onClick={signIn}
              expand="block"
              fill="clear"
              size="default"
              color="primary"
              strong={true}
            >
              {copy.signInButtonText}
            </IonButton>
            <p className="welcome__note">{copy.note}</p>
          </ButtonContainer>
        </Padding>

        {/* Onboarding */}
        <Onboarding isOpen={showOnboarding} delay={ONBOARDING_DELAY} />
      </IonContent>
    </IonPage>
  );
};

export default Welcome;
